import Api from '@/api';
import router, { createLinkToChat } from '@/router';
import Util from '@/util';

import { apiAction, apiFetch } from './apiState';

const state = {
  chats: [],
  chatData: {},
  clinics: [],
  companies: [],
  connectedClients: [],
};

const actions = {
  getChats: apiFetch(async ({ commit, rootGetters }) => {
    const response = await (!rootGetters.profile.admin ? Api.getChats() : Api.getAllChats());

    const chats = response.data;
    commit('CHATS_LOADED', chats);
    return chats;
  }),
  loadMessages: apiFetch(async ({ commit }, chatId) => {
    const response = !!chatId && await Api.getMessages(chatId);
    const messages = ((response || {}).data || []).reverse();

    commit('SET_CHAT_MESSAGES', {
      chatId,
      messages,
    });
    return messages;
  }),
  assignChat: apiAction(async ({ commit }, chatId) => {
    const response = await Api.assignChat(chatId);
    const chat = response.data.chat;

    commit('UPDATE_CHAT', chat);
    commit('ADD_CHAT_MESSAGE', response.data.message);

    router.push(createLinkToChat(chat));
    return chat;
  }),
  queueChat: apiAction(async ({ commit }, chatId) => {
    const response = await Api.queueChat(chatId);
    const chat = response.data.chat;

    commit('UPDATE_CHAT', chat);
    commit('ADD_CHAT_MESSAGE', response.data.message);

    router.push(createLinkToChat(chat));
    return chat;
  }),
  closeChat: apiAction(async ({ commit }, { chatId, selection, details }) => {
    if (selection) {
      await Api.sendFeedback(chatId, selection, details);
    }
    const response = await Api.closeChat(chatId);
    const chat = response.data;
    commit('UPDATE_CHAT', chat);

    router.push('/own');
    return chat;
  }),
  closeAppointmentChat: apiAction(async ({ commit }, { chatId, clinicId, appointmentTime, details }) => {
    const response = await Api.closeClinicAppointmentChat(chatId, clinicId, appointmentTime, details);
    const chat = response.data;

    commit('UPDATE_CHAT', chat);

    router.push('/appointment');
    return chat;
  }),
  // TODO: replace by closeChat
  cancelAppointmentChat: apiAction(async ({ commit }, { chatId }) => {
    const response = await Api.closeChat(chatId);
    const chat = response.data;

    commit('UPDATE_CHAT', chat);

    router.push('/appointment');
    return chat;
  }),
  startVideo: apiAction(async ({ commit }, chatId) => {
    const response = await Api.startVideo(chatId);
    const message = response.data;
    commit('ADD_CHAT_MESSAGE', message);

    return true;
  }),
  stopVideo: apiAction(async ({ commit }, chatId) => {
    const response = await Api.stopVideo(chatId);
    const message = response.data;
    commit('ADD_CHAT_MESSAGE', message);

    return true;
  }),
  getClinics: apiFetch(async ({ commit }) => {
    const response = await Api.getClinics();
    const clinics = response.data;

    commit('CLINICS_LOADED', clinics);
    return clinics;
  }),
  getCompanies: apiFetch(async ({ commit }) => {
    const response = await Api.getCompanies();
    const companies = response.data;

    commit('COMPANIES_LOADED', companies);
    return companies;
  }),
};

const mutations = {
  CHATS_LOADED: (state, chats) => {
    const createdAscSort = (a, b) => (a.createdAt < b.createdAt) ? -1 : 1;
    const closedDescSort = (a, b) => (a.closedAt > b.closedAt) ? -1 : 1;

    const groupedChats = chats.reduce((group, chat) => {
      group[chat.queue].push(chat);
      return group;
    }, {
      new: [],
      appointment: [],
      own: [],
      closed: [],
    });

    state.chats = [
      ...groupedChats.new.sort(createdAscSort),
      ...groupedChats.appointment.sort(createdAscSort),
      ...groupedChats.own.sort(createdAscSort),
      ...groupedChats.closed.sort(closedDescSort),
    ];
  },
  RESET_CHATS: (state) => {
    state.chats = [];
    state.chatData = {};
  },
  CHAT_LOGOUT: (state) => {
    state.chats = [];
    state.chatData = {};
    state.clinics = [];
    state.companies = [];
  },
  UPDATE_CHAT: (state, chat) => {
    const index = state.chats.findIndex(({chatId}) => chatId === chat.chatId);
    if (index >= 0) {
      state.chats.splice(index, 1, chat);
    } else {
      state.chats.push(chat);
    }

    mutations.CHATS_LOADED(state, state.chats);
  },
  REMOVE_CHAT: (state, chatId) => {
    const index = state.chats.findIndex((chat) => chat.chatId === chatId);
    if (index >= 0) {
      state.chats.splice(index, 1);
    }
  },
  SET_CHAT_INPUT: (state, {chatId, input}) => {
    state.chatData = {
      ...state.chatData,

      [chatId]: {
        ...state.chatData[chatId],

        input,
      },
    };
  },
  ADD_CHAT_MESSAGE: (state, message) => {
    const {
      chatId,
    } = message;

    const {
      messages = [],
      ...chatData
    } = state.chatData[chatId] || {};

    state.chatData = {
      ...state.chatData,

      [chatId]: {
        ...chatData,

        messages: [
          ...messages,
          processMessage(state, message),
        ]
      },
    };
  },
  SET_CHAT_MESSAGES (state, {chatId, messages}) {
    state.chatData = {
      ...state.chatData,

      [chatId]: {
        ...state.chatData[chatId],

        messages: messages.map((message) => processMessage(state, message)),
      },
    };
  },
  RESET_CHAT_UNSEEN_MESSAGES (state, {chatId}) {
    state.chatData = {
      ...state.chatData,

      [chatId]: {
        ...state.chatData[chatId],

        unseenMessages: 0,
      },
    };
  },
  INCREASE_CHAT_UNSEEN_MESSAGES (state, {chatId, unseenMessages}) {
    const {
      unseenMessages: messages = 0,
      ...chatData
    } = state.chatData[chatId] || {};

    state.chatData = {
      ...state.chatData,

      [chatId]: {
        ...chatData,

        unseenMessages: messages + (unseenMessages || 0),
      },
    };
  },
  WEBSOCKET_ADD_CLIENT (state, connectedUsers) {
    state.connectedClients = connectedUsers;
  },
  WEBSOCKET_REMOVE_CLIENT (state, disconnectedUserId) {
    const index = state.connectedClients.findIndex(client => client === disconnectedUserId);
    if (index >= 0) {
      state.connectedClients.splice(index, 1);
    }
  },
  CLINICS_LOADED (state, clinics) {
    state.clinics = clinics;
  },
  COMPANIES_LOADED (state, companies) {
    state.companies = companies;
  },
};

const getters = {
  chats: state => state.chats,
  newChats: state => state.chats.filter(({queue}) => queue === 'new'),
  appointmentChats: state => state.chats.filter(({queue}) => queue === 'appointment'),
  ownChats: state => state.chats.filter(({queue}) => queue === 'own'),
  closedChats: state => state.chats.filter(({queue}) => queue === 'closed'),
  chatById: state => chatId => state.chats.find((chat) => chat.chatId === chatId),

  chatMessagesById: state => chatId => (state.chatData[chatId] || {}).messages || [],
  chatUnseenMessagesById: state => chatId => (state.chatData[chatId] || {}).unseenMessages || 0,
  chatInputById: state => chatId => (state.chatData[chatId] || {}).input || '',

  newChatUnseenMessages: (state, getters) => getters.newChats.reduce((acc, {chatId}) => acc + getters.chatUnseenMessagesById(chatId), 0),
  ownChatUnseenMessages: (state, getters) => getters.ownChats.reduce((acc, {chatId}) => acc + getters.chatUnseenMessagesById(chatId), 0),

  videoToken: state => state.videoToken,
  connectedUsers: state => {
    return state.connectedClients.length === 0 ? '-' : state.connectedClients.length;
  },
  clinics: state => state.clinics,
  companies: state => state.companies,
  companyById: (state) => (id) => state.companies.find(company => company.id === id),
  videoCallTokenById: (state) => Object.entries(state.chatData).reduce((tokens, [chatId, chatData]) => {
    const videoMessage = [...(chatData.messages || [])].reverse().find((message) => ['videostart', 'videoend'].includes(message.type));
    const token = ((videoMessage || {}).__doctorParameters || {}).practitionerToken;
    if (token) {
      tokens[chatId] = token;
    }
    return tokens;
  }, {}),
};

function getPractitionerName(state, message) {
  const chat = state.chats.find(({chatId}) => chatId === message.chatId);
  const {
    practitioners = [],
  } = chat || {};

  return Util.getPractitionerName(practitioners, message);
}

function processMessage(state, message) {
  const {
    type,
  } = message;

  if (type === 'assign') {
    message.category = 'note';
    message.message = `${getPractitionerName(state, message)} otti keskustelun itselleen`;
  } else if (type === 'unassign') {
    message.category = 'note';
    message.message = `${getPractitionerName(state, message)} palautti keskustelun jonoon`;
  } else if (type === 'text') {
    if (message.message.startsWith('[LH1]') || (message.sourceId && message.sourceId.endsWith('_user_close'))) {
      message.category = 'note';
    }
  } else if (type === 'videostart') {
    message.category = 'note';
    message.message = 'Videoyhteys avattu';
  } else if (type === 'videoend') {
    message.category = 'note';
    message.message = (message.sourceId || '').endsWith('_user_video_close') ? message.message : 'Videoyhteys suljettu';
  } else if (type === 'end') {
    message.type = 'text';
    message.category = 'note';
  }

  if (message.message.startsWith('[LH1]')) {
    message.message = message.message.substring(5).trimStart();
  }

  return message;
}

export default {
  state,
  getters,
  actions,
  mutations,
};

